import type { GroupWithAll, MeetWithAll } from "../types.js";
import type {
  CustomDisciplineGroupSnapshot,
  CustomDisciplineValueSnapshot,
} from "./group-snapshot.js";

export const CUSTOM_DISCIPLINE_LIVE_EVENT = "custom-discipline-patch";
export type CustomValueDelta = {
  set: CustomDisciplineValueSnapshot[];
  remove: string[];
};
export type CustomAthleteDelta = {
  id: string;
  order?: number;
  values: CustomValueDelta;
};
export type CustomGroupDelta = {
  id: string;
  customGroupId: string;
  data: Partial<
    Pick<
      GroupWithAll,
      | "name"
      | "observations"
      | "plateauNumber"
      | "groupNumber"
      | "jury"
      | "secretary"
      | "referee1"
      | "referee2"
      | "centralReferee"
      | "customIsOver"
    >
  >;
  name?: string | null;
  values: CustomValueDelta;
  athletes: CustomAthleteDelta[];
  removedAthleteIds: string[];
};
export type CustomDisciplineLivePatch = {
  meetId: string;
  requestId: string;
  addedGroups: GroupWithAll[];
  removedGroupIds: string[];
  groups: CustomGroupDelta[];
  currentCustomAthleteId?: string | null;
};
export type CustomLiveMeet = Pick<
  MeetWithAll,
  "id" | "groups" | "currentCustomAthleteId"
>;
const groupKeys = [
  "name",
  "observations",
  "plateauNumber",
  "groupNumber",
  "jury",
  "secretary",
  "referee1",
  "referee2",
  "centralReferee",
  "customIsOver",
] as const;
const normalizeValue = (
  v: CustomDisciplineValueSnapshot,
): CustomDisciplineValueSnapshot => ({
  fieldDefinitionId: v.fieldDefinitionId,
  textValue: v.textValue ?? null,
  numberValue: v.numberValue == null ? null : Number(v.numberValue),
  selectOptionId: v.selectOptionId ?? null,
});
function valuesDelta(
  before: CustomDisciplineValueSnapshot[],
  after: CustomDisciplineValueSnapshot[],
): CustomValueDelta {
  const old = new Map(before.map((v) => [v.fieldDefinitionId, v]));
  const next = new Set(after.map((v) => v.fieldDefinitionId));
  return {
    set: after
      .filter(
        (v) =>
          JSON.stringify(normalizeValue(v)) !==
          JSON.stringify(
            old.has(v.fieldDefinitionId)
              ? normalizeValue(old.get(v.fieldDefinitionId)!)
              : null,
          ),
      )
      .map(normalizeValue),
    remove: before
      .filter((v) => !next.has(v.fieldDefinitionId))
      .map((v) => v.fieldDefinitionId),
  };
}
const changed = (v: CustomValueDelta) =>
  v.set.length > 0 || v.remove.length > 0;

/** The only whole-group payload is a newly created group. Schema/layouts never enter patches. */
export function buildCustomDisciplineLivePatch(
  before: CustomLiveMeet,
  after: CustomLiveMeet,
  requestId: string,
): CustomDisciplineLivePatch | null {
  if (before.id !== after.id) throw Error("Compétition du patch incohérente");
  const previous = new Map(
    before.groups.filter((g) => g.customDisciplineGroup).map((g) => [g.id, g]),
  );
  const next = new Map(
    after.groups.filter((g) => g.customDisciplineGroup).map((g) => [g.id, g]),
  );
  const patch: CustomDisciplineLivePatch = {
    meetId: after.id,
    requestId,
    addedGroups: [],
    removedGroupIds: [],
    groups: [],
  };
  for (const [id, group] of next) {
    const prev = previous.get(id),
      snapshot = group.customDisciplineGroup!;
    if (!prev) {
      patch.addedGroups.push({
        ...group,
        athletes: [],
        customDisciplineGroup: dataOnlySnapshot(snapshot),
      });
      continue;
    }
    if (
      snapshot.id !== prev.customDisciplineGroup!.id ||
      snapshot.groupTypeId !== prev.customDisciplineGroup!.groupTypeId
    )
      throw Error(
        "Le type d’un groupe existant ne peut pas changer dans un patch",
      );
    const data: CustomGroupDelta["data"] = {};
    for (const key of groupKeys)
      if (group[key] !== prev[key])
        (data as Record<string, unknown>)[key] = group[key];
    const values = valuesDelta(
      prev.customDisciplineGroup!.values,
      snapshot.values,
    );
    const oldAthletes = new Map(
      prev.customDisciplineGroup!.athletes.map((a) => [a.id, a]),
    );
    const newIds = new Set(snapshot.athletes.map((a) => a.id));
    const athletes = snapshot.athletes.flatMap((a) => {
      const old = oldAthletes.get(a.id),
        delta = valuesDelta(old?.values ?? [], a.values);
      return !old || a.order !== old.order || changed(delta)
        ? [
            {
              id: a.id,
              ...(a.order !== old?.order ? { order: a.order } : {}),
              values: delta,
            },
          ]
        : [];
    });
    const removedAthleteIds = [...oldAthletes.keys()].filter(
      (id) => !newIds.has(id),
    );
    const name =
      snapshot.name !== prev.customDisciplineGroup!.name
        ? { name: snapshot.name ?? null }
        : {};
    if (
      Object.keys(data).length ||
      Object.keys(name).length ||
      changed(values) ||
      athletes.length ||
      removedAthleteIds.length
    )
      patch.groups.push({
        id,
        customGroupId: snapshot.id,
        data,
        ...name,
        values,
        athletes,
        removedAthleteIds,
      });
  }
  patch.removedGroupIds = [...previous.keys()].filter((id) => !next.has(id));
  if (
    (before.currentCustomAthleteId ?? null) !==
    (after.currentCustomAthleteId ?? null)
  )
    patch.currentCustomAthleteId = after.currentCustomAthleteId ?? null;
  return patch.addedGroups.length ||
    patch.removedGroupIds.length ||
    patch.groups.length ||
    "currentCustomAthleteId" in patch
    ? patch
    : null;
}

export function dataOnlySnapshot(
  s: CustomDisciplineGroupSnapshot,
): CustomDisciplineGroupSnapshot {
  return {
    id: s.id,
    groupTypeId: s.groupTypeId,
    name: s.name ?? null,
    values: s.values.map(normalizeValue),
    athletes: s.athletes.map((a) => ({
      id: a.id,
      order: a.order,
      values: a.values.map(normalizeValue),
    })),
  };
}
function applyValues(
  before: CustomDisciplineValueSnapshot[],
  delta: CustomValueDelta,
) {
  const removed = new Set(delta.remove),
    values = new Map(delta.set.map((v) => [v.fieldDefinitionId, v]));
  const result = before
    .filter((v) => !removed.has(v.fieldDefinitionId))
    .map((v) => {
      const next = values.get(v.fieldDefinitionId);
      values.delete(v.fieldDefinitionId);
      return next &&
        JSON.stringify(normalizeValue(v)) !==
          JSON.stringify(normalizeValue(next))
        ? normalizeValue(next)
        : v;
    });
  result.push(...[...values.values()].map(normalizeValue));
  return result.length === before.length &&
    result.every((v, i) => v === before[i])
    ? before
    : result;
}

/** Immutable, shared server/browser reducer. Unchanged rows retain their identity. */
export function applyCustomDisciplineLivePatch<T extends CustomLiveMeet>(
  meet: T,
  patch: CustomDisciplineLivePatch,
): T {
  if (meet.id !== patch.meetId) throw Error("Compétition du patch incohérente");
  const removed = new Set(patch.removedGroupIds),
    changes = new Map(patch.groups.map((g) => [g.id, g]));
  const added = new Map(patch.addedGroups.map((g) => [g.id, g]));
  const groups = meet.groups
    .filter((g) => !removed.has(g.id))
    .map((group) => {
      if (added.has(group.id)) {
        const replacement = added.get(group.id)!;
        added.delete(group.id);
        return replacement;
      }
      const delta = changes.get(group.id);
      if (!delta) return group;
      const s = group.customDisciplineGroup;
      if (!s || s.id !== delta.customGroupId)
        throw Error("Groupe du patch absent ou incohérent");
      changes.delete(group.id);
      const athleteChanges = new Map(delta.athletes.map((a) => [a.id, a])),
        deleted = new Set(delta.removedAthleteIds);
      const athletes = s.athletes
        .filter((a) => !deleted.has(a.id))
        .map((a) => {
          const d = athleteChanges.get(a.id);
          if (!d) return a;
          athleteChanges.delete(a.id);
          const values = applyValues(a.values, d.values),
            order = d.order ?? a.order;
          return values === a.values && order === a.order
            ? a
            : { ...a, order, values };
        });
      for (const a of athleteChanges.values())
        athletes.push({
          id: a.id,
          order: a.order ?? 0,
          values: applyValues([], a.values),
        });
      const values = applyValues(s.values, delta.values);
      return {
        ...group,
        ...delta.data,
        customDisciplineGroup: {
          ...s,
          ...("name" in delta ? { name: delta.name } : {}),
          values,
          athletes,
        },
      };
    });
  if (changes.size) throw Error("Un groupe du patch doit être resynchronisé");
  groups.push(...added.values());
  return {
    ...meet,
    groups,
    ...("currentCustomAthleteId" in patch
      ? { currentCustomAthleteId: patch.currentCustomAthleteId }
      : {}),
  };
}

/** Reject malformed/oversized requests before they reach Prisma or the view store. */
export function validateCustomDisciplineLivePatch(
  value: unknown,
): asserts value is CustomDisciplineLivePatch {
  const p = value as CustomDisciplineLivePatch;
  const id = (v: unknown) =>
    typeof v === "string" && v.length > 0 && v.length <= 191;
  const ids = (v: unknown, max = 10000) =>
    Array.isArray(v) &&
    v.length <= max &&
    v.every(id) &&
    new Set(v).size === v.length;
  const delta = (v: CustomValueDelta) => {
    if (
      !v ||
      !Array.isArray(v.set) ||
      v.set.length > 1000 ||
      !ids(v.remove, 1000) ||
      new Set(v.set.map((i) => i?.fieldDefinitionId)).size !== v.set.length
    )
      throw Error("Valeurs du patch invalides");
    for (const x of v.set)
      if (
        !id(x?.fieldDefinitionId) ||
        (x.textValue != null &&
          (typeof x.textValue !== "string" || x.textValue.length > 65536)) ||
        (x.numberValue != null && !Number.isFinite(Number(x.numberValue))) ||
        (x.selectOptionId != null && !id(x.selectOptionId)) ||
        v.remove.includes(x.fieldDefinitionId)
      )
        throw Error("Valeur du patch invalide");
  };
  if (
    !p ||
    !id(p.meetId) ||
    !id(p.requestId) ||
    !Array.isArray(p.groups) ||
    p.groups.length > 1000 ||
    !Array.isArray(p.addedGroups) ||
    p.addedGroups.length > 1000 ||
    !ids(p.removedGroupIds, 1000) ||
    ("currentCustomAthleteId" in p &&
      p.currentCustomAthleteId !== null &&
      !id(p.currentCustomAthleteId))
  )
    throw Error("Patch personnalisé invalide");
  if (
    new Set([...p.groups, ...p.addedGroups].map((g) => g?.id)).size !==
    p.groups.length + p.addedGroups.length
  )
    throw Error("Groupe dupliqué dans le patch");
  for (const g of p.groups) {
    if (
      !id(g?.id) ||
      !id(g.customGroupId) ||
      !g.data ||
      Object.keys(g.data).some((k) => !groupKeys.includes(k as never)) ||
      !Array.isArray(g.athletes) ||
      g.athletes.length > 10000 ||
      new Set(g.athletes.map((a) => a?.id)).size !== g.athletes.length ||
      !ids(g.removedAthleteIds)
    )
      throw Error("Groupe du patch invalide");
    for (const [key, val] of Object.entries(g.data))
      if (
        key === "customIsOver"
          ? typeof val !== "boolean"
          : ["plateauNumber", "groupNumber"].includes(key)
            ? val != null && (!Number.isSafeInteger(val) || Number(val) < 0)
            : val != null && (typeof val !== "string" || val.length > 65536)
      )
        throw Error("Propriété du groupe invalide");
    if (
      "name" in g &&
      g.name !== null &&
      (typeof g.name !== "string" || g.name.length > 191)
    )
      throw Error("Nom du groupe invalide");
    delta(g.values);
    for (const a of g.athletes) {
      if (
        !id(a?.id) ||
        (a.order !== undefined &&
          (!Number.isSafeInteger(a.order) || a.order < 0)) ||
        g.removedAthleteIds.includes(a.id)
      )
        throw Error("Athlète du patch invalide");
      delta(a.values);
    }
  }
  for (const g of p.addedGroups)
    if (
      !id(g?.id) ||
      g.meet_id !== p.meetId ||
      !g.customDisciplineGroup ||
      g.customDisciplineGroupId !== g.customDisciplineGroup.id ||
      !Array.isArray(g.athletes) ||
      g.athletes.length > 0
    )
      throw Error("Nouveau groupe du patch invalide");
  if (
    p.removedGroupIds.some(
      (id) =>
        p.groups.some((g) => g.id === id) ||
        p.addedGroups.some((g) => g.id === id),
    )
  )
    throw Error("Suppression de groupe incohérente");
}
